import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { AuthPageLayout } from './AuthPageLayout';
import { Form, FormInput, FormItemGroup, FormLabel, FormError, SubmitButton } from './components/Form';

interface RequestResetFormData {
  email: string;
}

export function RequestPasswordResetPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<RequestResetFormData>();

  const onSubmit = async (data: RequestResetFormData) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/auth/request-password-reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: data.email }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to request password reset');
      }

      setSuccess(true);
      reset();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <AuthPageLayout>
        <div className="text-center">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Check your email
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            We've sent you a password reset link. Please check your email and follow the instructions.
          </p>
          <Link
            to="/login"
            className="text-sm font-medium text-blue-600 hover:text-blue-500"
          >
            Back to login
          </Link>
        </div>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout>
      <div className="text-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900">
          Reset your password
        </h2>
        <p className="text-sm text-gray-600 mt-2">
          Enter your email address and we'll send you a link to reset your password.
        </p>
      </div>
      
      <Form onSubmit={handleSubmit(onSubmit)}>
        <FormItemGroup>
          <FormLabel>E-mail</FormLabel>
          <FormInput
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address',
              },
            })}
            type="email"
            disabled={isLoading}
            placeholder="Enter your email"
          />
          {errors.email && <FormError>{errors.email.message}</FormError>}
        </FormItemGroup>
        
        {error && <FormError>{error}</FormError>}
        
        <FormItemGroup>
          <SubmitButton type="submit" disabled={isLoading}>
            {isLoading ? 'Sending...' : 'Send reset link'}
          </SubmitButton>
        </FormItemGroup>
      </Form>
      
      <div className="mt-4 text-center">
        <Link
          to="/login"
          className="text-sm font-medium text-blue-600 hover:text-blue-500"
        >
          Back to login
        </Link>
      </div>
    </AuthPageLayout>
  );
}
